// Setup › Workforce › Budgets (`/c/{slug}/setup/budgets`, `…/{id}`; UX pass 1.1 moved it from
// Workforce, whose `/agents/budgets` URLs redirect here; 11 §Budget hierarchy,
// §Controls; M3-08; managers): the budget levels (company → team → seat → DE → agent) with
// their amount, hard cap and alert thresholds, a new budget for a level, and each budget's spend
// against it (actual, reserved by live runs, open incidents), editable and archivable. "Spend
// this month" sums the runs' metered cost per DE from the runs feed. A DE's own budget is also
// its profile's Budget tab (`DeBudgetTab`). The budget routes are M3-04's: until they are
// registered on an API, the budget parts say so and the run spend still shows.
import { ApiError } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  buttonVariants,
  cn,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageHeader,
  Progress,
  QuickAddDialog,
  StatGrid,
  StatTile,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PiggyBankIcon, PlusIcon, WalletIcon } from "lucide-react";
import { type ReactNode, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { BudgetRow, DeBudget, DeProfile, RunRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { NotFoundPage } from "../../pages/status-pages";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { companyPeriodNow } from "../entry/calendar";
import { monthLabel } from "../finance/finance-parts";
import { formatDateTime, formatMoney, formatPercent, fractionDigits } from "../format";
import { digitalEmployeesQuery } from "../queries";
import { LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { useFeature } from "../use-feature";
import { isPendingRoute } from "../work/work-queries";
import { formatMicros, NotLiveYet, UsageCost, useCurrency } from "./agent-parts";
import {
  agentKeys,
  budgetQuery,
  budgetsQuery,
  deBudgetQuery,
  invalidateAgents,
  ROSTER_RUNS,
  runsQuery,
} from "./agent-queries";
import { BudgetPaceLine, BudgetPaceSettingsButton, BudgetPaceSettingsDialog } from "./budget-pace";

const LEVEL_LABELS: Record<BudgetRow["subject_kind"], string> = {
  company: "Company",
  team: "Team",
  seat: "Seat",
  holder: "Digital employee",
  agent: "Agent",
};
const PERIOD_LABELS: Record<BudgetRow["period_type"], string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
  standing: "Standing",
};

const PERIOD_ADVERB: Record<BudgetRow["period_type"], string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
  quarter: "Quarterly",
  year: "Yearly",
  standing: "Standing",
};

/** "Monthly · Oct 2026", "Weekly · 2026-W44", "Standing": a budget's period, as a row reads it. */
export function budgetPeriodText(b: Pick<BudgetRow, "period_type" | "period_key">): string {
  if (b.period_type === "standing" || !b.period_key) return PERIOD_ADVERB[b.period_type];
  const key = b.period_type === "month" ? monthLabel(b.period_key) : b.period_key;
  return `${PERIOD_ADVERB[b.period_type]} · ${key}`;
}

const currentMonth = () => new Date().toISOString().slice(0, 7);

/** Names of the budget subjects the app knows without a call: the company, DEs and their seats. */
function useSubjectNames(api: Api, company: CompanyAccess) {
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  return useMemo(() => {
    const names = new Map<string, string>([[company.id, company.name]]);
    for (const d of (des.data?.pages ?? []).flatMap((p) => p.data)) {
      // WORKFORCE: without a "DE · " label (the rows badge the name instead).
      names.set(d.membership_id, workerName(d.display_name));
      if (d.seat) names.set(d.seat.id, d.seat.title);
    }
    return names;
  }, [des.data, company.id, company.name]);
}

/** Major-unit text ("12.50") → minor units, or null when it is not an amount. */
function toMinor(text: string, currency: string): number | null {
  const n = Number(text.replace(/,/g, ""));
  if (!text.trim() || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 10 ** fractionDigits(currency));
}
const toMajor = (minor: number, currency: string) => String(minor / 10 ** fractionDigits(currency));

/** Spend against a budget: actual + reserved over the amount. */
function SpendBar({
  actualMicros,
  reservedMicros,
  amountMinor,
  currency,
}: {
  actualMicros: number;
  reservedMicros: number;
  amountMinor: number;
  currency: string;
}) {
  const amountMicros = amountMinor * 10 ** (6 - fractionDigits(currency));
  const used = amountMicros > 0 ? Math.min(1, actualMicros / amountMicros) : 1;
  return (
    <Progress value={Math.round(used * 100)} aria-label="Budget used" className="w-full">
      <span className="text-sm text-muted-foreground">
        {formatPercent(used)} used · {formatMicros(actualMicros, currency)} of{" "}
        {formatMoney(amountMinor, currency)}
        {reservedMicros ? ` · ${formatMicros(reservedMicros, currency)} reserved by live runs` : ""}
      </span>
    </Progress>
  );
}

export function BudgetsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useInfiniteQuery(budgetsQuery(api, company.id));
  const names = useSubjectNames(api, company);
  const live = !(q.isSuccess && q.data.pages[0] === null);
  const rows = (q.data?.pages ?? []).flatMap((p) => p?.data ?? []);
  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title="Budgets"
        badge={
          <InfoTip label="About Budgets">
            What each level may spend on agent work, when it warns and where it stops.
          </InfoTip>
        }
        actions={live && q.isSuccess ? <NewBudgetDialog api={api} company={company} /> : null}
      />
      {q.isPending ? (
        <Loading label="Loading budgets" rows={3} />
      ) : q.isError ? (
        <LoadError what="Budgets" error={q.error} />
      ) : !live ? (
        <Notice tone="info" title="Budgets are not live on this server yet">
          They arrive with the finance controls build (M3-04). The spend below is the runs' metered
          cost, which is live.
        </Notice>
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={PiggyBankIcon}
          title="No budgets yet"
          description="Without a budget a level inherits its parent's; set one on the company first, then on the DEs that need their own cap."
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table data-slot="budgets-table">
            <TableCaption>Active budgets, company first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Level</TableHead>
                <TableHead scope="col">Period</TableHead>
                <TableHead scope="col">Amount</TableHead>
                <TableHead scope="col">Hard cap</TableHead>
                <TableHead scope="col">Warns at</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...rows]
                .sort((a, b) => LEVEL_ORDER[a.subject_kind] - LEVEL_ORDER[b.subject_kind])
                .map((b) => (
                  <TableRow key={b.id} data-budget={b.id}>
                    <TableHead scope="row" className="font-medium">
                      <Link
                        to="/c/$slug/setup/budgets/$id"
                        params={{ slug: company.slug, id: b.id }}
                        search={keepFocus}
                        className="flex flex-col underline-offset-4 hover:underline"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          {names.get(b.subject_id) ?? LEVEL_LABELS[b.subject_kind]}
                          {b.subject_kind === "holder" ? <WorkerBadge kind="de" size="sm" /> : null}
                          {b.subject_kind === "agent" ? <WorkerBadge kind="ag" size="sm" /> : null}
                        </span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {LEVEL_LABELS[b.subject_kind]}
                        </span>
                      </Link>
                    </TableHead>
                    <TableCell>
                      {PERIOD_LABELS[b.period_type]}
                      {b.period_key ? ` · ${b.period_key}` : ""}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatMoney(b.amount_minor, b.currency)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {b.hard_cap_minor === null
                        ? stopsAtAmount(b)
                          ? "At amount"
                          : "None"
                        : formatMoney(b.hard_cap_minor, b.currency)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {b.soft_thresholds.map((t) => formatPercent(Number(t))).join(", ") || "—"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <LoadMore
            what="budgets"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
        </div>
      )}
      <SpendByDe api={api} company={company} />
    </>
  );
}

const LEVEL_ORDER: Record<BudgetRow["subject_kind"], number> = {
  company: 0,
  team: 1,
  seat: 2,
  holder: 3,
  agent: 4,
};

/** This month's metered run cost per DE, from the latest runs (needs the `agents` add-on). */
function SpendByDe({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  if (!agents.enabled) return null;
  return <SpendByDeTable api={api} company={company} />;
}

function SpendByDeTable({ api, company }: { api: Api; company: CompanyAccess }) {
  const runs = useInfiniteQuery(runsQuery(api, company.id, {}, ROSTER_RUNS));
  const names = useSubjectNames(api, company);
  const currency = useCurrency(api, company.id);
  const month = currentMonth();
  const rows = (runs.data?.pages[0]?.data ?? []).filter((r) => r.created_at.startsWith(month));
  const totals = spendByDe(rows);
  const truncated = Boolean(runs.data?.pages[0]?.next_cursor);
  return (
    <Section
      id="budgets-spend"
      title="Spend this month"
      description={`Metered run cost per digital employee${truncated ? `, from the latest ${ROSTER_RUNS} runs` : ""}.`}
    >
      {runs.isPending ? (
        <Loading label="Loading spend" rows={2} />
      ) : runs.isError ? (
        <LoadError what="Spend" error={runs.error} />
      ) : !totals.length ? (
        <p className="text-sm text-muted-foreground">No runs this month yet.</p>
      ) : (
        <Table data-slot="spend-table">
          <TableCaption>Run cost this month by digital employee.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Digital employee</TableHead>
              <TableHead scope="col">Runs</TableHead>
              <TableHead scope="col">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totals.map((t) => (
              <TableRow key={t.membershipId}>
                <TableHead scope="row" className="font-medium">
                  <Link
                    to="/c/$slug/agents/de/$id"
                    params={{ slug: company.slug, id: t.membershipId }}
                    search={(prev: Record<string, unknown>) => ({
                      ...keepFocus(prev),
                      tab: "budget" as const,
                    })}
                    className="inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
                  >
                    {names.get(t.membershipId) ?? "Digital employee"}
                    <WorkerBadge kind="de" size="sm" />
                  </Link>
                </TableHead>
                <TableCell className="tabular-nums">{t.runs}</TableCell>
                <TableCell>
                  <UsageCost usage={t} currency={currency} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

export function spendByDe(runs: readonly RunRow[]) {
  type Spend = {
    membershipId: string;
    runs: number;
    cost_micros: number;
    tokens_in: number;
    tokens_out: number;
  };
  const by = new Map<string, Spend>();
  for (const r of runs) {
    const t = by.get(r.membership_id) ?? {
      membershipId: r.membership_id,
      runs: 0,
      cost_micros: 0,
      tokens_in: 0,
      tokens_out: 0,
    };
    t.runs += 1;
    t.cost_micros += r.cost_micros;
    t.tokens_in += r.tokens_in;
    t.tokens_out += r.tokens_out;
    by.set(r.membership_id, t);
  }
  return [...by.values()].sort((a, b) => b.cost_micros - a.cost_micros);
}

/** A DE's own budget with no hard cap stops at its amount unless it is alert only. */
const stopsAtAmount = (b: Pick<BudgetRow, "subject_kind" | "alert_only">) =>
  b.subject_kind === "holder" && !b.alert_only;

/**
 * Amount, hard cap and warning threshold, as the budget forms share them. A DE's own budget
 * (`holder`) is a cap at its amount by default (DE-BUDGET-PACE): no new run starts into its last
 * reserve; "Alert only" turns that off, and a hard cap above the amount moves the stop there.
 */
function useBudgetFields(currency: string, initial?: Partial<BudgetRow>, holder = false) {
  const [alertOnly, setAlertOnly] = useState(initial?.alert_only ?? false);
  // BUDGET-ROLLOVER: on by default; the next period's budget is created when this one ends.
  const [repeat, setRepeat] = useState(initial?.repeat_monthly ?? true);
  const [amount, setAmount] = useState(
    initial?.amount_minor !== undefined ? toMajor(initial.amount_minor, currency) : "",
  );
  const [cap, setCap] = useState(
    initial?.hard_cap_minor != null ? toMajor(initial.hard_cap_minor, currency) : "",
  );
  // SEAT-DRAWER-UX §4.3: "hard cap or alert only" is one control. On: the cap's amount. Off:
  // `hard_cap_minor: null`, so crossing the warning alerts and nothing is refused or paused.
  const [capOn, setCapOn] = useState(initial?.hard_cap_minor != null);
  const [warn, setWarn] = useState(
    initial?.soft_thresholds?.[0]
      ? String(Math.round(Number(initial.soft_thresholds[0]) * 100))
      : "80",
  );
  const ids = {
    amount: useId(),
    cap: useId(),
    capOn: useId(),
    warn: useId(),
    alertOnly: useId(),
    repeat: useId(),
  };
  const noStop = holder && alertOnly;
  const amountMinor = toMinor(amount, currency);
  const capMinor = capOn && !noStop ? toMinor(cap, currency) : null;
  const warnPct = Number(warn);
  const valid =
    amountMinor !== null &&
    (!capOn || noStop || capMinor !== null) &&
    Number.isFinite(warnPct) &&
    warnPct > 0 &&
    warnPct <= 100;
  const body = {
    amount_minor: amountMinor ?? 0,
    hard_cap_minor: capMinor,
    soft_thresholds: [warnPct / 100],
    repeat_monthly: repeat,
    ...(holder ? { alert_only: alertOnly } : {}),
  };
  const fields: ReactNode = (
    <>
      <Field>
        <FieldLabel htmlFor={ids.amount}>Amount ({currency})</FieldLabel>
        <Input
          id={ids.amount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="500"
        />
      </Field>
      {holder ? (
        <Field>
          <div className="flex items-center gap-2">
            <Switch
              id={ids.alertOnly}
              checked={alertOnly}
              onCheckedChange={setAlertOnly}
              data-slot="budget-alert-only"
            />
            <FieldLabel htmlFor={ids.alertOnly}>Alert only (no stop)</FieldLabel>
          </div>
        </Field>
      ) : null}
      {noStop ? null : (
        <Field>
          <div className="flex items-center gap-2">
            <Switch
              id={ids.capOn}
              checked={capOn}
              onCheckedChange={setCapOn}
              data-slot="budget-cap-on"
            />
            <FieldLabel htmlFor={ids.capOn}>Hard cap</FieldLabel>
          </div>
          {capOn ? (
            <>
              <FieldLabel htmlFor={ids.cap}>Cap ({currency})</FieldLabel>
              <Input
                id={ids.cap}
                inputMode="decimal"
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                placeholder="600"
              />
              <FieldDescription>
                At the cap, new runs are refused until a raise is approved.
              </FieldDescription>
            </>
          ) : holder ? (
            <FieldDescription>Stops at the amount: no new runs in its last 1%.</FieldDescription>
          ) : (
            <FieldDescription>
              Alert only: crossing the warning notifies; nothing stops.
            </FieldDescription>
          )}
        </Field>
      )}
      <Field>
        <FieldLabel htmlFor={ids.warn}>Warn at (% used)</FieldLabel>
        <Input
          id={ids.warn}
          inputMode="numeric"
          value={warn}
          onChange={(e) => setWarn(e.target.value)}
        />
      </Field>
      <Field>
        <div className="flex items-center gap-2">
          <Switch
            id={ids.repeat}
            checked={repeat}
            onCheckedChange={setRepeat}
            data-slot="budget-repeat-monthly"
          />
          <FieldLabel htmlFor={ids.repeat}>Repeat monthly</FieldLabel>
        </div>
      </Field>
    </>
  );
  return { fields, valid, body };
}

function NewBudgetDialog({ api, company }: { api: Api; company: CompanyAccess }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="lg" onClick={() => setOpen(true)}>
        <PlusIcon aria-hidden="true" />
        New budget
      </Button>
      {open ? (
        <BudgetCreateDialog api={api} company={company} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/**
 * A new budget, as a dialog (MODALS: the list's "New budget" and the chart seat panel's "Set a
 * budget", which fixes the level to its seat). Saving closes it where it was opened and the
 * lists refresh; the budget's own page stays one click away in the list.
 */
export function BudgetCreateDialog({
  api,
  company,
  seat,
  month,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  /** A seat's budget: the level is fixed to it. */
  seat?: { id: string; title: string };
  /**
   * The month the caller shows (`2026-09`, the chart's finance month): the periods are that
   * month, its quarter and its year. Absent = today's.
   */
  month?: string | null;
  onClose: () => void;
}) {
  const monthKey = month ?? companyPeriodNow(company.calendar, "month");
  const [year, monthNo] = monthKey.split("-").map(Number) as [number, number];
  const qc = useQueryClient();
  const currency = useCurrency(api, company.id) ?? "USD";
  const des = useInfiniteQuery({ ...digitalEmployeesQuery(api, company.id), enabled: !seat });
  const list = (des.data?.pages ?? []).flatMap((p) => p.data);
  const [subject, setSubject] = useState(seat ? `seat:${seat.id}` : `company:${company.id}`);
  const [period, setPeriod] = useState<BudgetRow["period_type"]>("month");
  const ids = { subject: useId(), period: useId() };
  const f = useBudgetFields(currency, undefined, subject.startsWith("holder:"));
  // SEAT-DRAWER-UX §4.3: week and day budgets too, keyed in the company's calendar (its time
  // zone and week start, as the API enforces them). In the current month they are this week and
  // today; in another month, the week holding its 4th and its first day.
  const cal = company.calendar;
  const inMonth = monthKey === companyPeriodNow(cal, "month");
  const weekKey = companyPeriodNow(
    cal,
    "week",
    inMonth ? new Date() : new Date(`${monthKey}-04T12:00:00Z`),
  );
  const dayKey = inMonth ? companyPeriodNow(cal, "day") : `${monthKey}-01`;
  const create = useMutation({
    mutationFn: () => {
      const [kind, id] = subject.split(":") as [BudgetRow["subject_kind"], string];
      const key =
        period === "month"
          ? monthKey
          : period === "quarter"
            ? `${year}-Q${Math.floor((monthNo - 1) / 3) + 1}`
            : period === "year"
              ? String(year)
              : period === "week"
                ? weekKey
                : period === "day"
                  ? dayKey
                  : null;
      return api.client.budgets.create({
        params: { co: company.id },
        body: {
          subject_kind: kind,
          subject_id: id,
          period_type: period,
          period_key: key,
          currency: currency as BudgetRow["currency"],
          ...f.body,
        },
      });
    },
    onSuccess: () => {
      toast.add({ title: "Budget created", type: "success" });
      onClose();
    },
    onSettled: () => {
      invalidateAgents(qc, company.id);
      // The chart's seat setup and money, and budget vs actual (the panel's finance trend).
      void qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "finance"] });
    },
  });
  return (
    <QuickAddDialog
      title={seat ? "Set a budget" : "New budget"}
      info="One budget per level and period; runs reserve against every level above them."
      {...(seat
        ? { description: month ? `${seat.title} · ${monthLabel(monthKey)}` : seat.title }
        : {})}
      submitLabel="Create budget"
      canSubmit={f.valid}
      pending={create.isPending}
      onSubmit={() => create.mutate()}
      onClose={onClose}
      slot="budget-dialog"
      error={
        create.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(create.error, "The budget could not be created.")}
          </p>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        {seat ? null : (
          <Field>
            <FieldLabel htmlFor={ids.subject}>Level</FieldLabel>
            <NativeSelect
              id={ids.subject}
              className="w-full"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            >
              <NativeSelectOption value={`company:${company.id}`}>
                Company · {company.name}
              </NativeSelectOption>
              {list.map((d) => (
                <NativeSelectOption key={d.membership_id} value={`holder:${d.membership_id}`}>
                  Digital employee · {workerName(d.display_name)}
                </NativeSelectOption>
              ))}
              {list.flatMap((d) =>
                d.seat ? (
                  <NativeSelectOption key={`seat-${d.seat.id}`} value={`seat:${d.seat.id}`}>
                    Seat · {d.seat.title}
                  </NativeSelectOption>
                ) : (
                  []
                ),
              )}
            </NativeSelect>
          </Field>
        )}
        <Field>
          <FieldLabel htmlFor={ids.period}>Period</FieldLabel>
          <NativeSelect
            id={ids.period}
            className="w-full"
            value={period}
            onChange={(e) => setPeriod(e.target.value as typeof period)}
          >
            {month ? (
              <>
                <NativeSelectOption value="month">{monthLabel(monthKey)}</NativeSelectOption>
                <NativeSelectOption value="quarter">
                  Q{Math.floor((monthNo - 1) / 3) + 1} {year}
                </NativeSelectOption>
                <NativeSelectOption value="year">{year}</NativeSelectOption>
              </>
            ) : (
              <>
                <NativeSelectOption value="month">This month</NativeSelectOption>
                <NativeSelectOption value="quarter">This quarter</NativeSelectOption>
                <NativeSelectOption value="year">This year</NativeSelectOption>
              </>
            )}
            <NativeSelectOption value="week">
              {inMonth ? `This week (${weekKey})` : `Week ${weekKey}`}
            </NativeSelectOption>
            <NativeSelectOption value="day">
              {inMonth ? `Today (${dayKey})` : dayKey}
            </NativeSelectOption>
            <NativeSelectOption value="standing">Standing (no period)</NativeSelectOption>
          </NativeSelect>
        </Field>
        {f.fields}
      </div>
    </QuickAddDialog>
  );
}

export function BudgetDetailScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const q = useQuery(budgetQuery(api, company.id, id));
  const names = useSubjectNames(api, company);
  if (q.isError && isPendingRoute(q.error) && api.mode === "http")
    return (
      <NotLiveYet icon={PiggyBankIcon} what="Budgets" build="the finance controls build (M3-04)" />
    );
  if (q.isError && q.error instanceof ApiError && q.error.status === 404)
    return <NotFoundPage message="No such budget in this company." />;
  const b = q.data;
  const title = b ? (names.get(b.subject_id) ?? LEVEL_LABELS[b.subject_kind]) : "Budget";
  const amountMicros = b ? b.amount_minor * 10 ** (6 - fractionDigits(b.currency)) : 0;
  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/c/$slug/setup/budgets"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="underline-offset-4 hover:underline"
          >
            Budgets
          </Link>
        }
        title={title}
        badge={b ? <Badge variant="outline">{LEVEL_LABELS[b.subject_kind]}</Badge> : null}
        subtitle={
          b
            ? `${PERIOD_LABELS[b.period_type]}${b.period_key ? ` ${b.period_key}` : ""} · ${b.state === "active" ? "active" : "archived"}`
            : undefined
        }
        actions={
          b && b.state === "active" ? (
            <>
              <EditBudgetDialog api={api} company={company} budget={b} />
              <ArchiveBudget api={api} company={company} budget={b} />
            </>
          ) : null
        }
      />
      {q.isPending ? (
        <Loading label="Loading the budget" rows={3} />
      ) : q.isError ? (
        <LoadError what="The budget" error={q.error} />
      ) : b ? (
        <>
          <StatGrid data-slot="budget-stats">
            <StatTile
              label="Budget"
              value={formatMoney(b.amount_minor, b.currency)}
              icon={WalletIcon}
            />
            <StatTile label="Spent" value={formatMicros(b.actual_micros, b.currency)} />
            <StatTile
              label="Reserved by live runs"
              value={formatMicros(b.reserved_micros, b.currency)}
            />
            <StatTile
              label="Left"
              value={formatMicros(
                Math.max(0, amountMicros - b.actual_micros - b.reserved_micros),
                b.currency,
              )}
              detail={
                b.hard_cap_minor === null
                  ? stopsAtAmount(b)
                    ? "Stops at the amount"
                    : "No hard cap"
                  : `Hard cap ${formatMoney(b.hard_cap_minor, b.currency)}`
              }
            />
          </StatGrid>
          <div className="mt-6">
            <SpendBar
              actualMicros={b.actual_micros}
              reservedMicros={b.reserved_micros}
              amountMinor={b.amount_minor}
              currency={b.currency}
            />
          </div>
          <Section
            id="budget-incidents"
            title="Open incidents"
            description="Thresholds crossed and caps hit this period."
          >
            {b.incidents.length ? (
              <Table data-slot="budget-incidents">
                <TableCaption>Open budget incidents.</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">When</TableHead>
                    <TableHead scope="col">What</TableHead>
                    <TableHead scope="col">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {b.incidents.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="tabular-nums">{formatDateTime(i.created_at)}</TableCell>
                      <TableHead scope="row" className="font-medium">
                        {i.kind === "threshold"
                          ? `Passed ${formatPercent(Number(i.threshold ?? 0))}`
                          : i.kind === "cap"
                            ? "Hit the hard cap"
                            : "A reservation was refused"}
                      </TableHead>
                      <TableCell className="tabular-nums">
                        {formatMoney(i.amount_minor, b.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">None: spend is within the thresholds.</p>
            )}
          </Section>
          {b.subject_kind === "holder" ? (
            <Section id="budget-de" title="Digital employee">
              <Link
                to="/c/$slug/agents/de/$id"
                params={{ slug: company.slug, id: b.subject_id }}
                search={(prev: Record<string, unknown>) => ({
                  ...keepFocus(prev),
                  tab: "runs" as const,
                })}
                className={cn(buttonVariants({ variant: "secondary" }), "self-start")}
              >
                Open {title}'s runs
              </Link>
            </Section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function EditBudgetDialog({
  api,
  company,
  budget: b,
}: {
  api: Api;
  company: CompanyAccess;
  budget: BudgetRow;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="lg" onClick={() => setOpen(true)}>
        Edit budget
      </Button>
      {open ? (
        <BudgetEditDialog api={api} company={company} budget={b} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/**
 * Edit a budget's amount, cap and warning (SEAT-DRAWER-MODALS: the budget page's "Edit budget"
 * and the chart seat panel's Finance modal open the same dialog). Saving closes it.
 */
export function BudgetEditDialog({
  api,
  company,
  budget: b,
  description = "Changes apply to runs admitted from now on.",
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  budget: BudgetRow;
  description?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const f = useBudgetFields(b.currency, b, b.subject_kind === "holder");
  const save = useMutation({
    mutationFn: () =>
      api.client.budgets.update({ params: { co: company.id, id: b.id }, body: f.body }),
    onSuccess: () => {
      toast.add({ title: "Budget saved", type: "success" });
      onClose();
    },
    onSettled: () => {
      invalidateAgents(qc, company.id);
      // The chart's seat setup and money, and budget vs actual (the panel's finance trend).
      void qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "finance"] });
    },
  });
  return (
    <QuickAddDialog
      title="Edit budget"
      description={description}
      submitLabel="Save"
      canSubmit={f.valid}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="budget-edit-dialog"
      error={
        save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(save.error, "The budget could not be saved.")}
          </p>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">{f.fields}</div>
    </QuickAddDialog>
  );
}

function ArchiveBudget({
  api,
  company,
  budget: b,
}: {
  api: Api;
  company: CompanyAccess;
  budget: BudgetRow;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const archive = useMutation({
    mutationFn: () => api.client.budgets.archive({ params: { co: company.id, id: b.id } }),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  return (
    <>
      <Button
        variant="outline"
        size="lg"
        onClick={() => setOpen(true)}
        disabled={archive.isPending}
      >
        Archive
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this budget?</AlertDialogTitle>
            <AlertDialogDescription>
              The level inherits its parent's budget again. Its history is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                archive.mutate();
                setOpen(false);
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** A DE profile's Budget tab (managers): its holder budget, spend and reservations; set it. */
export function DeBudgetTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const q = useQuery(deBudgetQuery(api, company.id, de.membership_id));
  if (q.isPending) return <Loading label="Loading the budget" rows={2} />;
  if (q.isError) return <LoadError what="The budget" error={q.error} />;
  if (q.data === null)
    return (
      <Notice tone="info" title="DE budgets are not live on this server yet">
        They arrive with the finance controls build (M3-04).
      </Notice>
    );
  return <DeBudgetBody api={api} company={company} de={de} data={q.data} />;
}

function DeBudgetBody({
  api,
  company,
  de,
  data,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  data: DeBudget;
}) {
  const qc = useQueryClient();
  const fallback = useCurrency(api, company.id) ?? "USD";
  const currency = data.budget?.currency ?? fallback;
  const f = useBudgetFields(currency, data.budget ?? undefined, true);
  const save = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.updateBudget({
        params: { co: company.id, id: de.membership_id },
        body: {
          period_type: data.budget?.period_type ?? "month",
          period_key: data.budget?.period_key ?? currentMonth(),
          currency: currency as BudgetRow["currency"],
          ...f.body,
        },
      }),
    onSuccess: (next) => qc.setQueryData(agentKeys.deBudget(company.id, de.membership_id), next),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  const b = data.budget;
  const [paceOpen, setPaceOpen] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="de-budget">
      {data.paused_for_budget ? (
        <Notice tone="warning" title={`${workerName(de.display_name)} is paused: budget used up`}>
          New runs are refused until the budget is raised, an overrun is approved or the next period
          starts (it resumes by itself then).
        </Notice>
      ) : null}
      <div className="max-w-xl">
        <BudgetPaceLine
          pace={data.pace}
          currency={data.pace.currency ?? currency}
          settings={data.pace.settings}
          label={data.period_key ? `Pace · ${data.period_key}` : "Pace"}
          action={<BudgetPaceSettingsButton onClick={() => setPaceOpen(true)} />}
        />
      </div>
      {paceOpen ? (
        <BudgetPaceSettingsDialog
          api={api}
          company={company}
          deId={de.membership_id}
          deName={workerName(de.display_name)}
          onClose={() => setPaceOpen(false)}
        />
      ) : null}
      {b ? (
        <>
          <StatGrid>
            <StatTile
              label={`Budget${data.period_key ? ` · ${data.period_key}` : ""}`}
              value={formatMoney(b.amount_minor, b.currency)}
              icon={WalletIcon}
            />
            <StatTile label="Spent" value={formatMicros(data.actual_micros, b.currency)} />
            <StatTile
              label="Reserved by live runs"
              value={formatMicros(data.reserved_micros, b.currency)}
            />
          </StatGrid>
          <SpendBar
            actualMicros={data.actual_micros}
            reservedMicros={data.reserved_micros}
            amountMinor={b.amount_minor}
            currency={b.currency}
          />
          <Link
            to="/c/$slug/setup/budgets/$id"
            params={{ slug: company.slug, id: b.id }}
            search={keepFocus}
            className="self-start text-sm text-primary-text underline-offset-4 hover:underline"
          >
            Open the budget's incidents
          </Link>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          No budget of its own: {workerName(de.display_name)} spends under its seat's and the
          company's budgets.
        </p>
      )}
      <Section id="de-budget-set" title={b ? "Change the budget" : "Set a budget"}>
        <div className="grid max-w-xl gap-4">{f.fields}</div>
        {save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(save.error, "The budget could not be saved.")}
          </p>
        ) : null}
        {save.isSuccess ? (
          <p role="status" className="text-sm text-muted-foreground">
            Saved.
          </p>
        ) : null}
        <Button
          className="self-start"
          disabled={!f.valid || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : b ? "Save budget" : "Set budget"}
        </Button>
      </Section>
    </div>
  );
}
